import { useEffect, useState } from 'react';
import { servicesAPI, adminAPI } from '../api';
import { Settings, Edit2 } from 'react-feather';
import './AdminServices.css';

const SUPPLIER_ORDER = ['exo', 'smm_africa'];

function getSupplierLabel(supplierCode) {
  if (supplierCode === 'exo') return 'EXO';
  if (supplierCode === 'smm_africa') return 'SMM Africa';
  return supplierCode || 'Unknown Supplier';
}

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [editSvc, setEditSvc] = useState(null);
  const [editForm, setEditForm] = useState({ custom_price: '', status: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [multiplier, setMultiplier] = useState('1.3');
  const [savingMul, setSavingMul] = useState(false);
  const [mulMsg, setMulMsg] = useState('');
  const [syncing, setSyncing] = useState(false);

  const loadAll = (retries = 3) => {
    setLoading(true);

    const attemptLoad = (attempt = 0) => {
      Promise.all([servicesAPI.adminAll(), adminAPI.settings()])
        .then(([svcRes, settingsRes]) => {
          setServices(Array.isArray(svcRes.data?.services) ? svcRes.data.services : []);
          setMultiplier(settingsRes.data?.settings?.profit_multiplier || '1.3');
          setLoading(false);
        })
        .catch(err => {
          // Retry if we haven't exceeded max attempts and it's a server error
          if (attempt < retries && (err.response?.status >= 500 || !err.response)) {
            setTimeout(() => attemptLoad(attempt + 1), 1000 * (attempt + 1)); // Exponential backoff
          } else {
            setLoading(false);
            console.error('Failed to load services:', err);
          }
        });
    };

    attemptLoad();
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openEdit = (service) => {
    setEditSvc(service);
    setEditForm({
      custom_price: service.custom_price != null ? parseFloat(service.custom_price).toFixed(4) : '',
      status: service.status
    });
    setMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');

    try {
      await servicesAPI.adminUpdate(editSvc.id, {
        custom_price: editForm.custom_price === '' ? null : parseFloat(editForm.custom_price),
        status: editForm.status
      });

      setServices((prev) => prev.map((service) => (
        service.id === editSvc.id
          ? {
              ...service,
              custom_price: editForm.custom_price === '' ? null : editForm.custom_price,
              status: editForm.status
            }
          : service
      )));
      setMsg('Saved');
      setTimeout(() => setEditSvc(null), 1000);
    } catch (error) {
      setMsg(error.response?.data?.error || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const saveMul = async () => {
    setSavingMul(true);
    setMulMsg('');

    try {
      await adminAPI.updateSettings({ profit_multiplier: multiplier });
      setMulMsg('Multiplier saved. Re-sync services to apply.');
    } catch {
      setMulMsg('Failed to save');
    } finally {
      setSavingMul(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setMulMsg('');
    try {
      const res = await servicesAPI.adminSync();
      setMulMsg(`Sync completed. ${res.data?.total || 0} services currently in the catalog.`);
      loadAll();
    } catch (error) {
      setMulMsg(error.response?.data?.error || 'Service sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = services.filter((service) => {
    const query = search.toLowerCase();
    const matchSearch =
      !search ||
      service.name?.toLowerCase().includes(query) ||
      service.category?.toLowerCase().includes(query);
    const matchStatus = !filterStatus || service.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const groupedBySupplier = SUPPLIER_ORDER
    .map((supplierCode) => ({
      supplierCode,
      supplierLabel: getSupplierLabel(supplierCode),
      services: filtered.filter((service) => (service.supplier_code || 'exo') === supplierCode),
    }))
    .filter((group) => group.services.length > 0)
    .concat(
      Object.entries(
        filtered.reduce((acc, service) => {
          const supplierCode = service.supplier_code || 'exo';
          if (SUPPLIER_ORDER.includes(supplierCode)) return acc;
          if (!acc[supplierCode]) acc[supplierCode] = [];
          acc[supplierCode].push(service);
          return acc;
        }, {})
      ).map(([supplierCode, supplierServices]) => ({
        supplierCode,
        supplierLabel: getSupplierLabel(supplierCode),
        services: supplierServices,
      }))
    );

  return (
    <div className="admin-services-page">
      <h1 className="page-title">Services</h1>
      <p className="page-subtitle">
        {services.length} total services across {new Set(services.map((service) => service.supplier_code || 'exo')).size} suppliers
      </p>

      <div className="card admin-services-card" style={{ marginBottom: 24, maxWidth: 480 }}>
        <h3 style={{ marginBottom: 4, fontSize: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Settings size={18} /> Global Profit Multiplier
        </h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
          Final price = supplier price x multiplier. Applied to all services without a custom price.
        </p>
        {mulMsg && (
          <div className={`alert ${mulMsg.startsWith('Multiplier saved') ? 'alert-success' : 'alert-error'}`}>
            {mulMsg}
          </div>
        )}
        <div className="admin-services-actions" style={{ display: 'flex', gap: 10 }}>
          <input
            className="form-input"
            type="number"
            step="0.01"
            min="1"
            style={{ maxWidth: 140 }}
            value={multiplier}
            onChange={(e) => setMultiplier(e.target.value)}
          />
          <button className="btn btn-primary" onClick={saveMul} disabled={savingMul}>
            {savingMul ? 'Saving...' : 'Save Multiplier'}
          </button>
          <button className="btn btn-secondary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync Services'}
          </button>
        </div>
      </div>

      <div className="card admin-services-card">
        <div className="admin-services-filters" style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <input
            className="form-input"
            style={{ maxWidth: 280 }}
            placeholder="Search by name or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="form-select"
            style={{ maxWidth: 160 }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
          <div style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
            Showing {filtered.length} services in {groupedBySupplier.length} section{groupedBySupplier.length === 1 ? '' : 's'}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <div className="admin-services-sections">
            {groupedBySupplier.map(({ supplierCode, supplierLabel, services: supplierServices }) => (
              <section key={supplierCode} className="admin-services-supplier-section">
                <div className="admin-services-supplier-header">
                  <div>
                    <h3 className="admin-services-supplier-title">{supplierLabel}</h3>
                    <p className="admin-services-supplier-subtitle">
                      {supplierServices.length} service{supplierServices.length === 1 ? '' : 's'} from {supplierLabel}
                    </p>
                  </div>
                </div>
                <div className="table-wrap admin-services-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>{supplierLabel} ID</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Supplier Price</th>
                        <th>Calc Price</th>
                        <th>Custom Price</th>
                        <th>Final</th>
                        <th>Min</th>
                        <th>Max</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierServices.map((service) => {
                        const supplierPrice = parseFloat(service.supplier_price || 0);
                        const calcPrice = (supplierPrice * parseFloat(multiplier || 1)).toFixed(4);
                        const finalPrice = service.custom_price != null ? parseFloat(service.custom_price).toFixed(4) : calcPrice;

                        return (
                          <tr key={service.id}>
                            <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{service.id}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{service.supplier_service_id}</td>
                            <td
                              style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}
                              title={service.name}
                            >
                              {service.name}
                            </td>
                            <td style={{ fontSize: 12 }}>{service.category}</td>
                            <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>${supplierPrice.toFixed(4)}</td>
                            <td style={{ fontSize: 13 }}>${calcPrice}</td>
                            <td>
                              {service.custom_price != null ? (
                                <span style={{ color: 'var(--orange)', fontWeight: 600, fontSize: 13 }}>
                                  ${parseFloat(service.custom_price).toFixed(4)}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>-</span>
                              )}
                            </td>
                            <td style={{ color: 'var(--green-500)', fontWeight: 700, fontSize: 13 }}>${finalPrice}</td>
                            <td style={{ fontSize: 12 }}>{service.min?.toLocaleString?.() ?? service.min}</td>
                            <td style={{ fontSize: 12 }}>{service.max?.toLocaleString?.() ?? service.max}</td>
                            <td><span className={`badge badge-${service.status}`}>{service.status}</span></td>
                            <td>
                              <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={() => openEdit(service)}>
                                <Edit2 size={14} style={{ marginRight: 4 }} /> Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {editSvc && (
        <div className="modal-overlay" onClick={() => setEditSvc(null)}>
          <div className="modal admin-services-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Edit Service</h3>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-muted)',
                marginBottom: 18,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {editSvc.name}
            </div>
            {msg && (
              <div className={`alert ${msg.startsWith('Saved') ? 'alert-success' : 'alert-error'}`}>
                {msg}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Custom Price per 1000 (USD)</label>
              <input
                className="form-input"
                type="number"
                step="0.0001"
                min="0"
                placeholder={`Leave empty to use calculated (${(parseFloat(editSvc.supplier_price || 0) * parseFloat(multiplier || 1)).toFixed(4)})`}
                value={editForm.custom_price}
                onChange={(e) => setEditForm({ ...editForm, custom_price: e.target.value })}
              />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Supplier: ${parseFloat(editSvc.supplier_price || 0).toFixed(4)} x {multiplier} = $
                {(parseFloat(editSvc.supplier_price || 0) * parseFloat(multiplier || 1)).toFixed(4)}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditSvc(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
